import React from 'react';
import { Card } from './ui/Card';
import { Users, Calendar, ArrowUpDown, Activity, UserCheck, CalendarCheck } from 'lucide-react';

type StatCardProps = {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendUp?: boolean;
};

type SyncCardProps = {
  title: string;
  received: number;
  failed: number;
  icon: React.ReactNode;
};

const StatCard = ({ title, value, icon, trend, trendUp }: StatCardProps) => (
  <Card className="p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-semibold mt-1">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
            {trend}
          </p>
        )}
      </div>
      <div className="p-3 bg-indigo-50 rounded-lg">
        {icon}
      </div>
    </div>
  </Card>
);

const SyncCard = ({ title, received, failed, icon }: SyncCardProps) => {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className="p-3 bg-indigo-50 rounded-lg">
          {icon}
        </div>
      </div>
      <div className="flex items-center gap-6 mt-2">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-yellow-400"></span>
          <div>
            <span className="text-2xl font-semibold text-yellow-700">{received.toLocaleString()}</span>
            <span className="text-sm text-gray-500 ml-1">pending</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
          <div>
            <span className="text-2xl font-semibold text-red-700">{failed.toLocaleString()}</span>
            <span className="text-sm text-gray-500 ml-1">failed</span>
          </div>
        </div>
      </div>
      {received === 0 && failed === 0 && (
        <div className="flex items-center gap-1.5 mt-2">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
          <span className="text-lg text-green-700 font-medium">All synced!</span>
        </div>
      )}
    </Card>
  );
};

type DashboardStatsProps = {
  stats: {
    totalPatients: number;
    totalAppointments: number;
    syncRate: number;
    apiHealth: string;
    patientSync: {
      total: number;
      received: number;
      failed: number;
      synced: number;
    };
    appointmentSync: {
      total: number;
      received: number;
      failed: number;
      synced: number;
    };
  };
};

export default function DashboardStats({ stats }: DashboardStatsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Row 1 */}
      <StatCard
        title="Total Patients"
        value={stats.totalPatients}
        icon={<Users className="w-6 h-6 text-indigo-600" />}
      />
      <StatCard
        title="Total Appointments"
        value={stats.totalAppointments}
        icon={<Calendar className="w-6 h-6 text-indigo-600" />}
      />
      <StatCard
        title="Overall Sync Status"
        value={typeof stats.syncRate === 'number' ? `${stats.syncRate}%` : ''}
        icon={<ArrowUpDown className="w-6 h-6 text-indigo-600" />}
      />
      
      {/* Row 2 */}
      <SyncCard
        title="Patient Sync"
        received={stats.patientSync.received}
        failed={stats.patientSync.failed}
        icon={<UserCheck className="w-6 h-6 text-indigo-600" />}
      />
      <SyncCard
        title="Appointment Sync"
        received={stats.appointmentSync.received}
        failed={stats.appointmentSync.failed}
        icon={<CalendarCheck className="w-6 h-6 text-indigo-600" />}
      />
      <StatCard
        title="API Health"
        value={stats.apiHealth || 'Unknown'}
        icon={<Activity className="w-6 h-6 text-indigo-600" />}
      />
    </div>
  );
}